import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { useConversionCredits } from '@sorare/core/src/hooks/useConversionCredits';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import type { MonetaryAmountParams } from '@sorare/core/src/lib/monetaryAmount';

import { useCalculateAmounts } from 'hooks/useCalculateAmounts';
import { usePotentialDiscount } from 'hooks/usePotentialDiscount';

export const useConversionCreditForPrice = ({
  price,
  sport,
  forceRefetch,
  canDiscount,
}: {
  price: MonetaryAmountParams;
  sport: Sport;
  forceRefetch?: boolean;
  canDiscount?: boolean;
}) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  const monetaryAmount = toMonetaryAmount(price);

  const { conversionCredits } = useConversionCredits({ sport, forceRefetch });

  const filteredConversionCredits =
    conversionCredits?.filter(cc => !cc.disabled) || [];

  const canUseConversionCredit = filteredConversionCredits?.length > 0;

  const potentialDiscount = usePotentialDiscount(
    monetaryAmount,
    monetaryAmount.referenceCurrency,
    {
      canDiscount,
    }
  );

  const {
    totalMonetaryAmount: priceDiscountedAmount,
    conversionCreditMonetaryAmount,
    absoluteConversionCreditMonetaryAmount,
    maxDiscountMonetary,
  } = useCalculateAmounts({
    monetaryAmount,
    isFiat: false,
    creditCardFee: 0,
    activeFee: false,
    potentialDiscount,
  });

  return {
    canUseConversionCredit,
    priceDiscountedAmount,
    price,
    conversionCreditMonetaryAmount,
    absoluteConversionCreditMonetaryAmount,
    maxDiscountMonetary,
  };
};
